import React, { useRef } from 'react';
import type { CatalogEntry } from '../../services/inferenceApi';
import { CATALOG_TABS, tabCounts } from '../../utils/catalog';

interface CategoryTabsProps {
  models: CatalogEntry[];
  active: string;
  onChange: (key: string) => void;
}

export default function CategoryTabs({ models, active, onChange }: CategoryTabsProps) {
  const counts = tabCounts(models);
  const tabs = CATALOG_TABS.map((t) => ({ ...t, count: counts[t.key] })).filter(
    (t) => t.key === 'all' || t.count > 0
  );
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeIndex = Math.max(0, tabs.findIndex((t) => t.key === active));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    let nextIndex: number;
    switch (e.key) {
      case 'ArrowRight':
        nextIndex = (activeIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (activeIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const nextTab = tabs[nextIndex];
    if (nextTab.key !== active) onChange(nextTab.key);
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label="Model categories"
      onKeyDown={handleKeyDown}
      className="flex flex-wrap items-center gap-2"
    >
      {tabs.map((t, i) => {
        const isActive = active === t.key;
        return (
          <button
            key={t.key}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            role="tab"
            aria-selected={isActive}
            tabIndex={i === activeIndex ? 0 : -1}
            onClick={() => onChange(t.key)}
            className={`ps-chip shrink-0 motion-reduce:transition-none max-[820px]:min-h-[44px] ${isActive ? 'ps-chip-on' : ''}`}
          >
            {t.label}
            <span className="text-ps-subtle">
              {t.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}
